import 'swiper/swiper.scss';
import 'swiper/components/navigation/navigation.scss';

import React, { useEffect, useState } from 'react';
import SwiperCore, { Navigation } from 'swiper/core';
import { Swiper, SwiperSlide } from 'swiper/react';

import type { Asset2ReviewStatus, Asset2TranscodingStatus, Maybe } from '../../../../../apis/graphql';
import type { Asset2Extended } from '../../../../../models';
import Loader from '../../../../Loader';
import { AssetPreviewCard } from '../../molecules/AssetPreviewCard';
import type { Props as AssetPreviewCardProps } from '../../molecules/AssetPreviewCard/AssetPreviewCard';
import { NO_ASSETS_TEXT } from './constants';
import { EmptyMessage, LoaderWrapper, SwiperWrapper, Title } from './styles';

export type Props = {
  title: string;
  assets?: Asset2Extended[];
  loading?: boolean;
  titleFontSize?: string;
  creativeId?: string;
  derivedCreativeReviewStatus?: Asset2ReviewStatus;
  derivedCreativeTranscodingStatus?: Maybe<Asset2TranscodingStatus>;
};

type LazyAssetSlideProps = Omit<AssetPreviewCardProps, 'loadPreview'> & { isVisible: boolean };

// Swiper mounts every slide, so only start the asset download once a slide has been on screen.
// Once loaded it stays mounted, so paging back doesn't reset the player.
const LazyAssetSlide = ({ isVisible, ...cardProps }: LazyAssetSlideProps): React.JSX.Element => {
  const [hasBeenVisible, setHasBeenVisible] = useState(false);

  useEffect(() => {
    if (isVisible) {
      setHasBeenVisible(true);
    }
  }, [isVisible]);

  return <AssetPreviewCard {...cardProps} loadPreview={isVisible || hasBeenVisible} />;
};

export const AssetSlider = ({
  assets,
  title,
  titleFontSize,
  loading,
  creativeId,
  derivedCreativeReviewStatus,
  derivedCreativeTranscodingStatus,
}: Props): React.JSX.Element => {
  SwiperCore.use([Navigation]);

  const assetsComponent = assets?.length ? (
    assets.map((asset) => (
      <SwiperSlide key={asset.id}>
        {({ isVisible }): React.ReactNode => (
          <LazyAssetSlide
            isVisible={isVisible}
            asset={asset}
            creativeId={creativeId}
            derivedCreativeReviewStatus={derivedCreativeReviewStatus}
            derivedCreativeTranscodingStatus={derivedCreativeTranscodingStatus}
          />
        )}
      </SwiperSlide>
    ))
  ) : (
    <EmptyMessage>{NO_ASSETS_TEXT}</EmptyMessage>
  );

  return (
    <SwiperWrapper data-testid="asset-slider">
      <Title $fontSize={titleFontSize}>{title}</Title>
      {/* Swiper only reports slide visibility (SwiperSlide's isVisible) when watchSlidesVisibility is on. */}
      <Swiper navigation slidesPerView={3} slidesPerGroup={3} watchSlidesVisibility>
        {loading ? (
          <LoaderWrapper>
            <Loader />
          </LoaderWrapper>
        ) : (
          assetsComponent
        )}
      </Swiper>
    </SwiperWrapper>
  );
};
